/**
 * MAVLink 2 签名。
 *
 * 签名证明的是「这帧是谁发的」，不是「这帧有没有坏」—— 后者由 CRC 保证。
 * 因此校验失败**不应该**导致丢帧：地面站需要把这两件事分开呈现，
 * 否则排障时无法区分「链路有噪声」和「有台设备在冒充飞控发指令」。
 *
 * 签名块 13 字节的布局：
 *
 *   [0]      link_id
 *   [1..7]   timestamp（48 位小端，单位 10µs，自 2015-01-01 起算）
 *   [7..13]  SHA-256(secret_key[32] + 帧体 + link_id + timestamp) 的前 6 字节
 *
 * 「帧体」= STX 之后、签名之前的所有字节，即 头部(9) + 载荷 + CRC(2)。
 * 这个范围与上游 C 库的 `mavlink_sign_packet` 完全一致。
 */
import {
  MAVLINK_SIGNATURE_BLOCK_LENGTH,
  MAVLINK_SIGNATURE_HASH_LENGTH,
  MAVLINK_TIMESTAMP_LENGTH,
  SIGNING_EPOCH_MS,
  SIGNING_TICKS_PER_MS,
  SIGNING_TIMESTAMP_LIMIT,
  type FrameSignature,
} from './frame';
import { Sha256 } from './sha256';

export interface SigningKey {
  /** 32 字节密钥 */
  readonly secretKey: Uint8Array;
  /** 链路 ID（0–255）。多链路时用来区分"同一把密钥的不同通道"。 */
  readonly linkId: number;
}

/** 时间戳（48 位）默认容差：允许对端时钟比本机快 60 秒 */
export const DEFAULT_TIMESTAMP_TOLERANCE_MS = 60_000;

/**
 * 口令 → 32 字节密钥。
 *
 * 与 ArduPilot / QGC 一致：密钥就是口令的 SHA-256。这意味着"口令"长度不限，
 * 但密钥强度等于口令强度 —— 界面上必须说明这一点，短口令等于没有签名。
 */
export function deriveSecretKey(passphrase: string | Uint8Array): Uint8Array {
  const bytes = typeof passphrase === 'string' ? new TextEncoder().encode(passphrase) : passphrase;
  const hasher = new Sha256();
  hasher.update(bytes);
  return hasher.digest();
}

export function createSigningKey(passphrase: string | Uint8Array, linkId = 0): SigningKey {
  if (!Number.isInteger(linkId) || linkId < 0 || linkId > 255) {
    throw new RangeError(`链路 ID 必须是 0–255 之间的整数，收到 ${linkId}`);
  }
  const secretKey = deriveSecretKey(passphrase);
  if (secretKey.length !== 32) {
    throw new Error('签名密钥必须是 32 字节');
  }
  return { secretKey, linkId };
}

function writeTimestamp48(target: Uint8Array, offset: number, value: number): void {
  for (let index = 0; index < MAVLINK_TIMESTAMP_LENGTH; index += 1) {
    target[offset + index] = Math.floor(value / 2 ** (8 * index)) & 0xff;
  }
}

function readTimestamp48(source: Uint8Array, offset: number): number {
  let value = 0;
  for (let index = 0; index < MAVLINK_TIMESTAMP_LENGTH; index += 1) {
    value += (source[offset + index] ?? 0) * 2 ** (8 * index);
  }
  return value;
}

/** 本机时钟 → 签名时间戳（10µs 单位，48 位回绕） */
export function signingTimestampOf(nowMs: number): number {
  const ticks = Math.floor((nowMs - SIGNING_EPOCH_MS) * SIGNING_TICKS_PER_MS);
  // 48 位是协议位宽，回绕是预期行为（约 892 年后才会发生）
  return ((ticks % SIGNING_TIMESTAMP_LIMIT) + SIGNING_TIMESTAMP_LIMIT) % SIGNING_TIMESTAMP_LIMIT;
}

/** 签名时间戳 → 本机时钟毫秒 */
export function signingTimestampToMs(timestamp: number): number {
  return timestamp / SIGNING_TICKS_PER_MS + SIGNING_EPOCH_MS;
}

/**
 * 签名器。
 *
 * 时间戳必须**单调递增**：对端会用"时间戳回退"判定重放。
 * 同一毫秒内连发多帧时，如果都用 `Date.now()` 就会得到相同的时间戳，
 * 因此这里维护一个只增不减的计数器，必要时在上一帧的基础上 +1。
 */
export class MavlinkSigner {
  private lastTimestamp = 0;
  private readonly key: SigningKey;
  private readonly now: () => number;

  constructor(key: SigningKey, now: () => number = () => Date.now()) {
    this.key = key;
    this.now = now;
  }

  get linkId(): number {
    return this.key.linkId;
  }

  /** 生成 13 字节签名块。`frameBody` 是 STX 之后、签名之前的全部字节。 */
  sign(frameBody: Uint8Array): Uint8Array {
    const candidate = signingTimestampOf(this.now());
    this.lastTimestamp =
      candidate > this.lastTimestamp
        ? candidate
        : (this.lastTimestamp + 1) % SIGNING_TIMESTAMP_LIMIT;

    const block = new Uint8Array(MAVLINK_SIGNATURE_BLOCK_LENGTH);
    block[0] = this.key.linkId;
    writeTimestamp48(block, 1, this.lastTimestamp);

    const hasher = new Sha256();
    hasher.update(this.key.secretKey);
    hasher.update(frameBody);
    hasher.update(block.subarray(0, 1 + MAVLINK_TIMESTAMP_LENGTH));
    block.set(
      hasher.digest().subarray(0, MAVLINK_SIGNATURE_HASH_LENGTH),
      1 + MAVLINK_TIMESTAMP_LENGTH,
    );

    return block;
  }
}

export interface SignatureVerifierOptions {
  /** 允许对端时钟超前本机的毫秒数 */
  readonly toleranceMs?: number;
}

export interface SignatureRejection {
  readonly reason:
    | 'unknown_link'
    | 'bad_signature'
    | 'timestamp_in_future'
    | 'timestamp_replayed';
  readonly detail: string;
}

/**
 * 签名校验器。
 *
 * 只对**配了密钥的链路**做校验；没配密钥的链路直接放行 ——
 * 绝大多数 ArduPilot 部署不开签名，把"未签名"当成"不可信"会让地面站在默认配置下不可用。
 * 界面上以"链路是否受签名保护"的独立状态呈现，而不是混进数据可信度。
 */
export class MavlinkSignatureVerifier {
  private readonly keys: readonly SigningKey[];
  private readonly toleranceMs: number;
  /** 每个链路已接受的最大时间戳，用于拒绝重放 */
  private readonly lastAccepted = new Map<number, number>();

  constructor(keys: readonly SigningKey[], options: SignatureVerifierOptions = {}) {
    this.keys = keys;
    this.toleranceMs = options.toleranceMs ?? DEFAULT_TIMESTAMP_TOLERANCE_MS;
  }

  get enabled(): boolean {
    return this.keys.length > 0;
  }

  verify(
    frameBody: Uint8Array,
    signature: FrameSignature,
    nowMs: number,
  ): SignatureRejection | null {
    if (!this.enabled) return null;

    const key = this.keys.find((candidate) => candidate.linkId === signature.linkId);
    if (key === undefined) {
      return { reason: 'unknown_link', detail: `未配置链路 ID ${signature.linkId} 的密钥` };
    }

    const hasher = new Sha256();
    hasher.update(key.secretKey);
    hasher.update(frameBody);
    const tail = new Uint8Array(1 + MAVLINK_TIMESTAMP_LENGTH);
    tail[0] = signature.linkId;
    writeTimestamp48(tail, 1, signature.timestamp);
    hasher.update(tail);

    const digest = hasher.digest();
    // 比较全部字节再判断，避免把"匹配了几个字节"泄漏进时序
    let difference = 0;
    for (let index = 0; index < MAVLINK_SIGNATURE_HASH_LENGTH; index += 1) {
      difference |= (digest[index] ?? 0) ^ (signature.hash[index] ?? 0);
    }
    if (difference !== 0) {
      return { reason: 'bad_signature', detail: '签名不匹配，可能不是同一把密钥' };
    }

    const frameMs = signingTimestampToMs(signature.timestamp);
    if (frameMs > nowMs + this.toleranceMs) {
      return {
        reason: 'timestamp_in_future',
        detail: `帧时间戳比本机时钟超前 ${Math.round(frameMs - nowMs)} ms`,
      };
    }

    const previous = this.lastAccepted.get(signature.linkId);
    if (previous !== undefined && signature.timestamp < previous) {
      return { reason: 'timestamp_replayed', detail: '时间戳早于本链路已接受的最新值，疑似重放' };
    }

    this.lastAccepted.set(signature.linkId, signature.timestamp);
    return null;
  }

  /** 断链重连后调用：新链路的序号与时间戳从零开始，旧的"已接受"记录会误判为重放 */
  reset(): void {
    this.lastAccepted.clear();
  }
}

export { readTimestamp48, writeTimestamp48 };
